import { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useRoleInfo } from 'hooks/useRoleInfo';
import {
  getFinalSubmissions,
  submitFinalEvaluationAsDraft,
  updateFinalEvaluation,
  getExistingFinalJudgments,
  completeAllFinalEvaluations,
} from 'services/finalEvaluatorService';
import { getPillarDisplayName } from 'constants/awardCategoryPillar';
import { Submission } from 'types/evaluation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export interface EvaluationData {
  isRevision?: boolean;
  existingJudgmentId?: string;
  submission?: Submission;
  [key: string]: any;
}

export const useFinalEvaluator = () => {
  const { profile, isAuthenticated } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const currentYear = new Date().getFullYear();
  const EARLIEST_YEAR = 2025;

  const [year, setYear] = useState<number>(currentYear);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [existingJudgments, setExistingJudgments] = useState<
    Record<string, any>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isCompletingAll, setIsCompletingAll] = useState<boolean>(false);
  const [isRefreshingJudgments, setIsRefreshingJudgments] =
    useState<boolean>(false);

  const {
    userPillar,
    validUserCategories,
    userIsPillarPIC,
    userIsSuperAdmin,
    userIsCategoryJudge,
  } = useRoleInfo();

  const userPillarDisplayName = userPillar
    ? getPillarDisplayName(userPillar)
    : null;

  // Determine access control based on role type
  const userHasAccess =
    userIsSuperAdmin || (userIsCategoryJudge && validUserCategories.length > 0); // Award admins or JUDGE_CATEGORY with valid categories can access

  // Success message function that fades away
  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const fetchFinalSubmissions = async (fetchYear: number = year) => {
    try {
      setLoading(true);
      setError(null);

      const result = await getFinalSubmissions(fetchYear);
      let allSubmissions: Submission[] = result.data || [];

      // Apply role-based category filtering FIRST - support multiple categories
      if (validUserCategories.length > 0) {
        allSubmissions = allSubmissions.filter(submission =>
          validUserCategories.includes(submission.category)
        );
      } else {
        console.warn('⚠️ No valid categories for user, showing no submissions');
        allSubmissions = [];
      }

      // Apply pillar conflict-of-interest filtering for JUDGE_CATEGORY users
      if (
        userIsCategoryJudge &&
        userPillarDisplayName &&
        allSubmissions.length > 0
      ) {
        allSubmissions = allSubmissions.filter(
          submission => submission.pillar !== userPillarDisplayName
        );
      } else if (userIsCategoryJudge && !userPillar) {
        console.warn(
          '⚠️ No judge pillar found - skipping pillar COI filtering'
        );
      }

      // Get existing judgments for this judge
      const judgeEmail = profile?.email;
      let judgments: Record<string, any> = {};

      if (judgeEmail && allSubmissions.length > 0) {
        judgments = await getExistingFinalJudgments(allSubmissions, judgeEmail);
      } else if (!judgeEmail) {
        console.warn('⚠️ No judge email found - skipping judgment fetching');
      }

      setSubmissions(allSubmissions);
      setExistingJudgments(judgments);

      if (allSubmissions.length === 0) {
        let errorMsg;
        if (judgeEmail && Object.keys(judgments).length > 0) {
          // Judge has completed all submissions
          const categoriesText =
            validUserCategories.length > 1
              ? `"${validUserCategories.join('", "')}" categories`
              : `"${validUserCategories[0]}" category`;
          errorMsg = userPillarDisplayName
            ? `All final submissions in the ${categoriesText} have been completed (excluding ${userPillarDisplayName} pillar due to conflict of interest)`
            : `All final submissions in the ${categoriesText} have been completed`;
        } else {
          // No submissions available at all
          const categoriesText =
            validUserCategories.length > 1
              ? `"${validUserCategories.join('", "')}" categories`
              : `"${validUserCategories[0]}" category`;
          errorMsg = userPillarDisplayName
            ? `No final submissions found in the ${categoriesText} (excluding ${userPillarDisplayName} pillar due to conflict of interest)`
            : `No final submissions found in the ${categoriesText}`;
        }

        setError(errorMsg);
      }
    } catch (err: any) {
      console.error('❌ Error fetching final submissions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch final submissions on component mount
  useEffect(() => {
    if (userHasAccess) {
      fetchFinalSubmissions(year);
    }
  }, [userHasAccess, year]);

  const handleEvaluationSubmit = async (evaluationData: EvaluationData) => {
    try {
      setError(null);

      // Prepare judge information from auth context
      const judgeInfo = {
        name: profile?.fullName || 'Unknown Judge',
        email: profile?.email || 'unknown@email.com',
      };

      // reCAPTCHA is not required for internal evaluations

      let result;

      if (evaluationData.isRevision && evaluationData.existingJudgmentId) {
        // Update existing evaluation
        result = await updateFinalEvaluation(
          evaluationData.existingJudgmentId,
          evaluationData,
          judgeInfo,
          true // Always save as draft for now
        );
      } else {
        // Create new draft evaluation
        result = await submitFinalEvaluationAsDraft(evaluationData, judgeInfo);
      }

      if (result.success) {
        // Show success message without auto-navigation
        const action = evaluationData.isRevision ? 'updated' : 'saved';

        showSuccessMessage(
          `✅ Evaluation ${action} as draft successfully! You can continue reviewing or navigate manually.`
        );

        // Refresh judgments after submission
        const refreshJudgments = async () => {
          try {
            setIsRefreshingJudgments(true);

            const judgeEmail = profile?.email;
            if (judgeEmail && submissions.length > 0) {
              // Update judgments in background
              const updatedJudgments = await getExistingFinalJudgments(
                submissions,
                judgeEmail
              );
              setExistingJudgments(updatedJudgments);
              setIsRefreshingJudgments(false);
            }
          } catch (err) {
            console.warn('Failed to refresh judgments:', err);
            setIsRefreshingJudgments(false);
          }
        };

        // Minimal delay for better UX
        setTimeout(refreshJudgments, 200);
      } else {
        throw new Error(
          result.error || 'Unknown error occurred during submission'
        );
      }
    } catch (err: any) {
      console.error('❌ Error submitting evaluation:', err);

      // Provide detailed error feedback
      let errorMessage = 'Failed to save evaluation';

      if (err.message.includes('No judging data found')) {
        errorMessage = `Category "${evaluationData.submission?.category || 'Unknown'}" is not configured for judgment. Please contact system administrator.`;
      } else if (err.message.includes('Submission not found')) {
        errorMessage =
          'The submission could not be found. It may have been deleted or moved.';
      } else if (err.message.includes('No valid aspect scores')) {
        errorMessage =
          'The evaluation scores could not be mapped to the category rubric. Please try again.';
      } else if (err.message.includes('Failed to fetch submission')) {
        errorMessage =
          'Unable to fetch submission details. Please check your network connection.';
      } else {
        errorMessage = `${errorMessage}: ${err.message}`;
      }

      setError(errorMessage);
    }
  };

  // Handler for completing all draft evaluations
  const handleCompleteAll = async () => {
    try {
      setIsCompletingAll(true);
      setError(null);

      // Get all judgment IDs that are drafts
      const draftJudgmentIds = Object.values(existingJudgments)
        .filter(
          judgment =>
            judgment.overall_summary === 'Draft Final' ||
            judgment.overall_summary === 'Draft'
        )
        .map(judgment => judgment.documentId);

      if (draftJudgmentIds.length === 0) {
        showSuccessMessage('No draft evaluations to complete.');
        setIsCompletingAll(false);
        return;
      }

      const judgeInfo = {
        name: profile?.fullName || 'Unknown Judge',
        email: profile?.email || 'unknown@email.com',
      };

      const result = await completeAllFinalEvaluations(
        draftJudgmentIds,
        judgeInfo
      );

      if (result.success) {
        showSuccessMessage(
          `✅ All ${result.successfulCount} evaluations completed successfully!`
        );

        // Refresh data to show updated statuses and then re-enable button
        setTimeout(async () => {
          try {
            await fetchFinalSubmissions(year);
          } catch (refreshError) {
            console.error(
              '❌ Error refreshing data after completion:',
              refreshError
            );
          } finally {
            setIsCompletingAll(false);
          }
        }, 1500);
      } else {
        const errorMsg = `Failed to complete some evaluations. ${result.successfulCount}/${result.totalProcessed} completed successfully.`;
        console.error('❌ Completion errors:', result.errors);
        setError(errorMsg);
        setIsCompletingAll(false);
      }
    } catch (err: any) {
      console.error('❌ Error completing all evaluations:', err);
      setError(`Failed to complete evaluations: ${err.message}`);
      setIsCompletingAll(false);
    }
  };

  return {
    isAuthenticated,
    userHasAccess,
    userIsPillarPIC,
    loading,
    year,
    setYear,
    EARLIEST_YEAR,
    currentYear,
    successMessage,
    isRefreshingJudgments,
    submissions,
    existingJudgments,
    handleCompleteAll,
    isCompletingAll,
    handleEvaluationSubmit,
    error,
    fetchFinalSubmissions,
    validUserCategories,
  };
};
